import { useState } from "react";
import { AlertTriangle } from "lucide-react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type {
  StockItemDetail,
  StockLocation,
} from "@shared-core/data/api-types";
import { ApiError } from "@shared-core/data/api/errors";
import { batchLabel } from "./AdjustStockDialog";
import { Button } from "../../app/components/primitives/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../app/components/primitives/dialog";
import {
  FieldShell,
  type FieldErrorMap,
  TextField,
  selectClassName,
  toFieldErrorMap,
} from "./StockFormField";

/** What the dialog edits: an existing holding, or a new warehouse to record. */
export interface PositionTarget {
  location_id: string | null;
  batch_id: string | null;
  position: string | null;
}

interface StockPositionDialogProps {
  item: StockItemDetail;
  target: PositionTarget;
  /** Active warehouses, offered when recording a position somewhere new. */
  locations: StockLocation[];
  onClose: () => void;
  onSaved: (detail: StockItemDetail) => void;
}

export function StockPositionDialog({
  item,
  target,
  locations,
  onClose,
  onSaved,
}: StockPositionDialogProps) {
  const dataAdapter = useDataAdapter();
  const isNew = target.location_id == null;
  const [locationId, setLocationId] = useState(target.location_id ?? "");
  const [batchId, setBatchId] = useState(target.batch_id ?? "");
  const [position, setPosition] = useState(target.position ?? "");
  const [fieldErrors, setFieldErrors] = useState<FieldErrorMap>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const holding = item.holdings.find(
    (h) => h.location_id === locationId && h.batch_id === (batchId || null),
  );

  async function handleSave() {
    if (!locationId) {
      setFieldErrors({ location_id: "Choose a warehouse" });
      return;
    }
    if (item.tracks_batches && !batchId) {
      setFieldErrors({ batch_id: "Choose which batch" });
      return;
    }
    setIsSaving(true);
    setFieldErrors({});
    setFormError(null);
    try {
      onSaved(
        await dataAdapter.setStockHoldingPosition(item.id, {
          location_id: locationId,
          batch_id: item.tracks_batches ? batchId : null,
          position: position.trim() || null,
        }),
      );
    } catch (error) {
      console.error("stock.holding.position_failed", {
        status: error instanceof ApiError ? error.status : undefined,
      });
      const map = toFieldErrorMap(error);
      setFieldErrors(map);
      setFormError(
        Object.keys(map).length > 0
          ? "Please fix the highlighted fields and try again."
          : "Couldn't save the bin. Please try again.",
      );
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{isNew ? "Record a bin" : "Edit bin"}</DialogTitle>
          <DialogDescription>
            Where exactly the stock sits in the warehouse (the bin), so the
            stock can be found when a supplier or another site is asked to ship
            the stock.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {formError && (
            <div
              role="alert"
              className="flex items-start gap-2 rounded-lg border border-red-300 bg-red-50 dark:bg-red-900/20 dark:border-red-800 p-3 text-sm text-red-700 dark:text-red-300"
            >
              <AlertTriangle className="size-4 shrink-0 mt-0.5" />
              {formError}
            </div>
          )}

          {isNew ? (
            <FieldShell
              id="position-location"
              label="Warehouse"
              error={fieldErrors.location_id}
            >
              {(aria) => (
                <select
                  id="position-location"
                  className={selectClassName}
                  value={locationId}
                  onChange={(e) => setLocationId(e.target.value)}
                  {...aria}
                >
                  <option value="">Choose a warehouse…</option>
                  {locations.map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.name}
                    </option>
                  ))}
                </select>
              )}
            </FieldShell>
          ) : (
            <p className="text-sm">
              <span className="text-muted-foreground">Warehouse: </span>
              {holding?.location_name ?? holding?.location_id}
              {holding?.batch_no != null && (
                <>
                  {" "}
                  · {batchLabel(holding.batch_no, holding.artwork_revision_id)}
                </>
              )}
            </p>
          )}

          {isNew && item.tracks_batches && (
            <FieldShell
              id="position-batch"
              label="Batch"
              error={fieldErrors.batch_id}
            >
              {(aria) => (
                <select
                  id="position-batch"
                  className={selectClassName}
                  value={batchId}
                  onChange={(e) => setBatchId(e.target.value)}
                  {...aria}
                >
                  <option value="">Choose a batch…</option>
                  {item.batches
                    .filter((b) => !b.withdrawn_at)
                    .map((b) => (
                      <option key={b.id} value={b.id}>
                        {batchLabel(b.batch_no, b.artwork_revision_id)}
                      </option>
                    ))}
                </select>
              )}
            </FieldShell>
          )}

          <TextField
            id="position-text"
            label="Bin"
            value={position}
            onChange={setPosition}
            error={fieldErrors.position}
            placeholder="e.g. Aisle 3, bay 2, shelf C"
            hint="Leave blank to clear it."
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={() => void handleSave()} disabled={isSaving}>
            {isSaving ? "Saving…" : "Save bin"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
